/**
 * R7.2.2C1 — Route-level error boundary for account modules.
 * A single lazy/API failure must not blank the whole Mini App.
 */
import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";

type Props = {
  children: ReactNode;
  routeName: string;
  lang?: StoreV2Lang;
  onRetry?: () => void;
  onGoHome?: () => void;
  onGoBack?: () => void;
};

type State = { failed: boolean; remountKey: number };

const WRAP: CSSProperties = {
  padding: 24,
  textAlign: "center",
  color: "#0f1115",
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

export class CustomerRouteErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false, remountKey: 0 };

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  override componentDidCatch(_error: Error, _info: ErrorInfo): void {
    try {
      console.warn("customer_route_error", { route: this.props.routeName });
      const w = window as Window & {
        __AS_CUSTOMER_NAV_DIAG__?: { route_errors: number };
      };
      w.__AS_CUSTOMER_NAV_DIAG__ = w.__AS_CUSTOMER_NAV_DIAG__ ?? { route_errors: 0 };
      w.__AS_CUSTOMER_NAV_DIAG__.route_errors += 1;
    } catch {
      /* ignore */
    }
  }

  private retry = () => {
    this.setState((s) => ({ failed: false, remountKey: s.remountKey + 1 }));
    this.props.onRetry?.();
  };

  override render(): ReactNode {
    if (this.state.failed) {
      const en = this.props.lang === "en";
      return (
        <div style={WRAP} data-testid="sv2-route-error" data-route={this.props.routeName}>
          <p style={{ fontSize: 18, fontWeight: 700, margin: "8px 0" }}>
            {en ? "Could not open this section." : "Не вдалося відкрити цей розділ."}
          </p>
          <p style={{ fontSize: 14, margin: "0 0 16px", color: "#5b616e" }}>
            {en ? "Try again or return home." : "Спробуйте ще раз або поверніться на головну."}
          </p>
          <button
            type="button"
            data-testid="sv2-route-error-retry"
            onClick={this.retry}
            style={{
              minHeight: 48,
              minWidth: 200,
              border: 0,
              borderRadius: 14,
              background: "#2563eb",
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {en ? "Try again" : "Спробувати ще раз"}
          </button>
          {this.props.onGoBack ? (
            <button
              type="button"
              data-testid="sv2-route-error-back"
              onClick={this.props.onGoBack}
              style={{
                display: "block",
                margin: "12px auto 0",
                minHeight: 44,
                minWidth: 200,
                border: "1px solid rgba(125,129,138,0.35)",
                borderRadius: 14,
                background: "transparent",
                fontSize: 15,
              }}
            >
              {en ? "Back" : "Назад"}
            </button>
          ) : null}
          {this.props.onGoHome ? (
            <button
              type="button"
              data-testid="sv2-route-error-home"
              onClick={this.props.onGoHome}
              style={{
                display: "block",
                margin: "12px auto 0",
                minHeight: 44,
                minWidth: 200,
                border: "1px solid rgba(125,129,138,0.35)",
                borderRadius: 14,
                background: "transparent",
                fontSize: 15,
              }}
            >
              {en ? "Home" : "На головну"}
            </button>
          ) : null}
        </div>
      );
    }
    return <div key={this.state.remountKey}>{this.props.children}</div>;
  }
}
